import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { eventSearchApi } from '../utils/eventSearchApi';
import { fetchEventTours } from '../../../../../actions/event-template-actions';
import { processPrismicAds } from '../utils/prismicAdProcessor';

export const useEventSearch = () => {
  const [tours, setTours] = useState({
    allEventTours: [],
    availableTours: [],
  });
  const eventTours = useSelector(state => state?.eventTemplates?.tours || []);
  const auth = useSelector(state => state.auth);
  const search = useSelector(state => state.search);
  const dispatch = useDispatch();

  useEffect(() => {
    getTourAggregations();
    if (eventTours?.length === 0) {
      dispatch(fetchEventTours());
    }
  }, []);

  useEffect(() => {
    if (eventTours?.length > 0 && tours?.availableTours?.length > 0) {
      setTours({
        allEventTours: eventTours,
        availableTours: tours?.availableTours,
      });
    }
  }, [eventTours, tours?.availableTours]);

  const [state, setState] = useState({
    events: {
      hits: [],
      total: 0,
      totalAllowed: 0,
      skip: 0,
    },
    loading: false,
    error: null,
    pageLoadedOnce: false,
    isLoadingMore: false,
  });

  const searchEvents = async (filters, query = '') => {
    setState(prev => ({
      ...prev,
      loading: true,
      error: null,
      isLoadingMore: false,
    }));

    try {
      const searchParams = {
        query,
        filters: {
          ...filters,
          type: 'events', // Ensure we're always searching for events
        },
        top: 40, // Default page size for events
        skip: 0,
        appendType: true,
      };

      const result = await eventSearchApi.search(searchParams);

      // Process prismic ads for power users
      const userIsPowered = auth?.user?.isPowered || false;
      const eventsWithPrismicAds = userIsPowered
        ? processPrismicAds(result.hits || [], search.prismicContent)
        : result.hits || [];

      setState(prev => ({
        ...prev,
        events: {
          hits: eventsWithPrismicAds,
          total: result.total || 0,
          totalAllowed: result.totalAllowed || result.total || 0,
          skip: 40, // Next skip value
        },
        loading: false,
        pageLoadedOnce: true,
      }));

      return result;
    } catch (error) {
      console.error('Event search failed:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Search failed',
        pageLoadedOnce: true,
      }));
      throw error;
    }
  };

  const loadMoreEvents = async (filters, query = '') => {
    if (state.isLoadingMore || state.loading) {
        return;
    }

    setState(prev => ({ ...prev, isLoadingMore: true, error: null }));

    try {
      const searchParams = {
        query,
        filters: {
          ...filters,
          type: 'events',
        },
        top: 40,
        skip: state.events.skip,
        appendType: true,
      };

      const result = await eventSearchApi.search(searchParams);

      // For load more, we don't add prismic ads - they should only appear in initial results
      setState(prev => ({
        ...prev,
        events: {
          ...prev.events,
          hits: [...prev.events.hits, ...(result.hits || [])],
          skip: prev.events.skip + 40,
        },
        isLoadingMore: false,
      }));

      return result;
    } catch (error) {
      console.error('Load more events failed:', error);
      setState(prev => ({
        ...prev,
        isLoadingMore: false,
        error: error.message || 'Failed to load more events',
      }));
      throw error;
    }
  };

  const getTourAggregations = async () => {
    try {
      const tourAggregations = await eventSearchApi.getTourAggregations();
      setTours({
        ...tours,
        availableTours: tourAggregations,
      });
    } catch (error) {
      console.error('Failed to fetch tour aggregations:', error);
      throw error;
    }
  };

  const resetSearch = () => {
    setState({
      events: {
        hits: [],
        total: 0,
        totalAllowed: 0,
        skip: 0,
      },
      loading: false,
      error: null,
      pageLoadedOnce: false,
      isLoadingMore: false,
    });
  };

  return {
    events: state.events,
    loading: state.loading,
    error: state.error,
    pageLoadedOnce: state.pageLoadedOnce,
    isLoadingMore: state.isLoadingMore,
    searchEvents,
    loadMoreEvents,
    resetSearch,
    tours,
  };
};
